import { create } from "zustand";
import {
  CommonNotificationType,
  INotificationBaseMessage,
  VersionAction,
} from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";

import { IdleManager } from "tools/idle";
import { compare } from "compare-versions";

type State = {
  version: string;
  isLoading: boolean;
  lastSnackbarUpdate: number;
  isOpenModal: boolean;
};

type Action = {
  fetch: (options?: { silent?: boolean }) => Promise<void>;
  checkBuildVersion: () => Promise<void>;
  autoCatch: () => Promise<void>;
};

// The version baked into this bundle by vite.config.js. Compared against the
// version.json published next to index.html — when a newer build has been
// deployed under a long-lived tab, the two diverge and we trigger the same
// snackbar that the manual `apiV2.version` flow uses.
const BUILT_VERSION: string =
  typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "";

// Background poll interval. Backstop for sessions that never go idle long
// enough to fire IdleManager (60s threshold) — e.g. continuous data entry.
const BUILD_VERSION_POLL_MS = 5 * 60 * 1000;
// Module-level guard so re-invocations of autoCatch (e.g. HMR, multiple
// component mounts) don't stack intervals.
let buildVersionPollHandle: ReturnType<typeof setInterval> | null = null;

export const useVersion = create<State & Action>((set, get) => ({
  version: "",
  isLoading: false,
  lastSnackbarUpdate: 0,
  isOpenModal: false,

  autoCatch: async () => {
    const { fetch, checkBuildVersion } = get();

    IdleManager.getInstance().addSubscriber(
      "version",
      async () => {
        await Promise.all([fetch(), checkBuildVersion()]);
      },
      { keepAcrossSessions: true }
    );

    apiV2.notification.subscribe<INotificationBaseMessage>({
      key: CommonNotificationType.VERSION_UPDATED,
      callback: async () => {
        await fetch();
      },
    });

    if (!buildVersionPollHandle && !import.meta.env.DEV && BUILT_VERSION) {
      buildVersionPollHandle = setInterval(() => {
        get().checkBuildVersion();
      }, BUILD_VERSION_POLL_MS);
    }
  },

  checkBuildVersion: async () => {
    // Dev has no published version.json and __APP_VERSION__ is a fixed value
    // for the running dev server — nothing to compare.
    if (import.meta.env.DEV || !BUILT_VERSION) return;

    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-store",
      });
      if (!res.ok) return;
      const { version: deployed } = (await res.json()) as { version?: string };
      if (!deployed || deployed === BUILT_VERSION) return;

      // Bundle is older than what's deployed — soft-prompt the user to reload.
      // The manual apiV2.version flow can still escalate to POPUP separately.
      set(() => ({ lastSnackbarUpdate: Date.now() }));
    } catch {
      // Network errors during an idle ping are not actionable.
    }
  },

  fetch: async ({ silent = false } = {}) => {
    const { isLoading } = get();

    if (isLoading) {
      return;
    }

    set(() => ({ version: localStorage.getItem("ffair_v") || "" }));
    set(() => ({ isLoading: true }));

    try {
      const { major, minor, patch, action } = await apiV2.version.getLatest();

      const version = `${major}.${minor}.${patch}`;

      localStorage.setItem("ffair_v", version);

      if (!silent && get().version && compare(version, get().version, ">")) {
        if (action === VersionAction.NOTIFICATION) {
          set(() => ({
            lastSnackbarUpdate: Date.now(),
          }));
        }

        if (action === VersionAction.POPUP) {
          set(() => ({
            isOpenModal: true,
          }));
        }
      }

      set(() => ({
        version,
      }));
    } catch (e) {
      console.error(e);
    } finally {
      set(() => ({ isLoading: false }));
    }
  },
}));
